import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api, ApiClientError } from '@/lib/api-client';
import { syncedLocationKeys } from './synced-locations';
import type { Enrichment, EnrichmentRequest, EnrichmentSaveResponse } from '@/types/api';

export const enrichmentKeys = {
  all: ['enrichment'] as const,
  detail: (syncedLocationId: string) => [...enrichmentKeys.all, 'detail', syncedLocationId] as const,
};

export function useEnrichment(syncedLocationId: string) {
  return useQuery({
    queryKey: enrichmentKeys.detail(syncedLocationId),
    queryFn: async () => {
      try {
        return await api.get<Enrichment>(`/api/v1/enrichment/${syncedLocationId}`);
      } catch (error) {
        // 404 means no enrichment exists yet - return null instead of throwing
        if (error instanceof ApiClientError && error.statusCode === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!syncedLocationId,
    retry: false,
    staleTime: 0, // Always refetch to ensure fresh enrichment data
  });
}

export function useSaveEnrichment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ syncedLocationId, data }: { syncedLocationId: string; data: EnrichmentRequest }) =>
      api.put<EnrichmentSaveResponse>(`/api/v1/enrichment/${syncedLocationId}`, data),
    onSuccess: (_, { syncedLocationId }) => {
      void queryClient.invalidateQueries({ queryKey: enrichmentKeys.detail(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}

export function useDeleteEnrichment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (syncedLocationId: string) =>
      api.delete<{ success: boolean }>(`/api/v1/enrichment/${syncedLocationId}`),
    onSuccess: (_, syncedLocationId) => {
      void queryClient.invalidateQueries({ queryKey: enrichmentKeys.detail(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(syncedLocationId) });
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    },
  });
}
